import React, { memo, useCallback, useState } from "react";
import { Link } from "react-router-dom/dist";
import { useAuth } from "../../../hooks/useAuth";
import { formatCurrency } from "../../../util/formatters";
import { getProductImage } from "../../../util/images";
import { showSuccess } from "../../../util/toast";
import { SITE_URLS, DIMENSIONS } from "../../../util/constants";
import VideoModal from "../../../components/VideoModal";

// Mantido para compatibilidade com código existente
export const hasImage = (img) => {
  return getProductImage(img);
};

const handleShare = (id) => {
  navigator.clipboard.writeText(SITE_URLS.PRODUCT_DETAIL(id));
  showSuccess("Copiado para área de transferência!");
};

const ProductItem = memo((obj) => {
  const { isLoggedIn } = useAuth();
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  const handleShareClick = useCallback(() => {
    handleShare(obj.id);
  }, [obj.id]);

  const handleVideoClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (obj.youtube_link) {
      setIsVideoModalOpen(true);
    }
  }, [obj.youtube_link]);

  const handleCloseVideoModal = useCallback(() => {
    setIsVideoModalOpen(false);
  }, []);

  const CartButton = isLoggedIn ? (
    <div className="btn-wrapper mt-2" style={{ display: "flex", gap: "10px", alignItems: "center" }}>
      <Link
        to={`/produto/${obj.id}`}
        style={{ flex: 1 }}
        aria-label={`Ver detalhes do produto ${obj.product_name}`}
        title={`Ver mais — ${obj.product_name ?? "produto"}`}
      >
        <button
          className="btn-small theme-btn-1 btn-effect-1 w-100"
          type="button"
          title="Ver mais"
        >
          <i className="fa-solid fa-cart-shopping" aria-hidden="true"></i>
          Ver mais
        </button>
      </Link>
      <button
        onClick={handleShareClick}
        style={{
          cursor: "pointer",
          fontSize: "20px",
          color: "var(--ltn__secondary-color)",
          padding: "8px",
          background: "transparent",
          border: "none",
        }}
        className="fa-solid fa-share-nodes"
        title="Compartilhar produto"
        aria-label="Compartilhar link do produto"
        type="button"
      ></button>
    </div>
  ) : (
    <div className="btn-wrapper mt-2 text-align-center">
      <Link
        to="/autenticacao"
        aria-label="Fazer login para ver mais informações do produto"
        title="Faça o login para mais informações"
      >
        <button
          className="btn-small theme-btn-1 btn-effect-1 w-100"
          type="button"
          title="Faça o login para mais informações"
        >
          Faça o login para mais informações
        </button>
      </Link>
    </div>
  );

  const hasVideo = obj.youtube_link;

  return (
    <>
      <div key={obj.id} className="col-xs-12 col-sm-4 col-md-4 mb-4 h-100">
        <div
          className="ltn__form-box  box-shadow white-bg"
          style={{ display: "flex", justifyContent: "center", position: "relative" }}
        >
          <Link
            to={isLoggedIn ? `/produto/${obj.id}` : "#"}
            style={{ display: "block", width: "100%" }}
            title={
              isLoggedIn
                ? `Ver produto — ${obj.product_name ?? ""}`.trim()
                : "Faça login para ver o produto"
            }
          >
            <img
              src={hasImage(obj.image)}
              alt={obj.product_name ? `${obj.product_name} - Código ${obj.product_id} | Grupo Terra` : `Produto código ${obj.product_id}`}
              style={{
                height: DIMENSIONS.IMAGE.PRODUCT_HEIGHT,
                width: "100%",
                objectFit: "contain",
              }}
              loading="lazy"
            />
          </Link>
          {(obj.promotional_payment_methods?.length > 0) && (
            <div
              style={{
                position: "absolute",
                top: "10px",
                left: "10px",
                backgroundColor: "rgba(25, 135, 84, 0.1)",
                border: "1px solid var(--bs-success, #198754)",
                color: "var(--bs-success, #198754)",
                fontSize: "0.7rem",
                padding: "4px 8px",
                borderRadius: "4px",
                fontWeight: "bold",
                zIndex: 2,
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
              title={`Forma de pagamento promocional: ${obj.promotional_payment_methods.map((m) => m.payment_name).join(", ")}`}
            >
              <i className="fa-solid fa-tag" aria-hidden="true" />
              Promoção
            </div>
          )}
          {hasVideo && (
            <div
              onClick={!isLoggedIn ? handleVideoClick : undefined}
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                backgroundColor: "rgba(0, 0, 0, 0.6)",
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 2,
                cursor: !isLoggedIn ? "pointer" : "default",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) => {
                if (!isLoggedIn) {
                  e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.8)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoggedIn) {
                  e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.6)";
                }
              }}
              title={!isLoggedIn ? "Clique para ver o vídeo" : "Este produto possui vídeo"}
              role={!isLoggedIn ? "button" : undefined}
              tabIndex={!isLoggedIn ? 0 : undefined}
              onKeyDown={!isLoggedIn ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleVideoClick(e);
                }
              } : undefined}
            >
              <i
                className="fa-solid fa-video"
                style={{
                  color: "#fff",
                  fontSize: "18px",
                }}
              />
            </div>
          )}
        </div>
        <div className="ltn__form-box detail-box box-shadow py-3 px-2">
          <i style={{ fontSize: '.75rem' }}>Código {`${formatCurrency(
            obj.product_id
          )}`}</i>
          <h3 className="product-label ltn__primary-color" style={{ fontSize: "1.1rem", marginBottom: "10px" }}>
            <Link
              to={isLoggedIn ? `/produto/${obj.id}` : "/autenticacao"}
              aria-label={`Ver detalhes do produto ${obj.product_name}`}
              title={`${obj.product_name ?? "Produto"} — ${isLoggedIn ? "ver detalhes" : "fazer login"}`}
            >
              {obj.product_name}
            </Link>
          </h3>
          {CartButton}
        </div>
      </div>
      {!isLoggedIn && hasVideo && (
        <VideoModal
          isOpen={isVideoModalOpen}
          onClose={handleCloseVideoModal}
          product={obj}
        />
      )}
    </>
  );
});

ProductItem.displayName = "ProductItem";
export default ProductItem;
